// ✅ THEME + UX FIXES (NO business/API logic changes)
// Updated your FIRST component to match the “below theme”:
// FIX 1: Rounded corners for ALL tables (outer + nested) even when horizontally scrollable
// FIX 2: Clip header corners properly (no square header corners)
// FIX 3: Keep your existing StudentKey-based logic + row-click expand intact
// NOTE: Endpoints remain the same as your first code (/api/studentattendance/*)

import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  LinearProgress,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { API_URL } from "./config";

export default function StudentAttendanceReport() {
  // ✅ Theme constants (match “below theme”)
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";

  const token = localStorage.getItem("auth");

  const [campusCode, setCampusCode] = useState("");
  const [username, setUsername] = useState("");
  const [studentKey, setStudentKey] = useState("");

  // view mode
  const [viewMode, setViewMode] = useState("day"); // "class" | "day"

  // ===== Class View state =====
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [summaryRows, setSummaryRows] = useState([]);
  const [openSemesters, setOpenSemesters] = useState({});
  const [subjectData, setSubjectData] = useState({});

  // ===== Day View state =====
  const [loadingDay, setLoadingDay] = useState(false);
  const [dayRows, setDayRows] = useState([]);
  const [openDayKeys, setOpenDayKeys] = useState({});
  const [dayDates, setDayDates] = useState({});

  const [toast, setToast] = useState({ open: false, type: "success", msg: "" });

  const convertToISTDate = (utcTime) =>
    utcTime
      ? new Date(utcTime).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" })
      : "";

  useEffect(() => {
    if (!token) return;
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      setCampusCode(payload.campusCode || payload.CampusCode || "");
      setUsername(payload.username || "");
      setStudentKey(payload.studentkey || "");
    } catch (e) {
      console.error("Failed to decode token", e);
      setToast({ open: true, type: "error", msg: "Invalid session token." });
    }
  }, [token]);

  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const pct = (val) => {
    const num = Number(val);
    if (!Number.isFinite(num)) return 0;
    return Math.max(0, Math.min(100, num));
  };

  const buildParams = useCallback(
    (extra = {}) => ({
      CampusCode: campusCode || undefined,
      StudentKey: studentKey || undefined,
      ...extra,
    }),
    [campusCode, studentKey]
  );

  const ensureStudentKey = () => {
    if (!studentKey) {
      setToast({ open: true, type: "error", msg: "StudentKey is missing in the token." });
      return false;
    }
    return true;
  };

  // ===== Fetch summary (class) =====
  const fetchSummary = useCallback(async () => {
    if (!ensureStudentKey()) return;

    setLoadingSummary(true);
    setSummaryRows([]);
    setOpenSemesters({});
    setSubjectData({});
    try {
      const { data } = await axios.get(`${API_URL}/api/studentattendance/summary`, {
        ...authHeader,
        params: buildParams(),
      });
      setSummaryRows(Array.isArray(data) ? data : []);
      if (!Array.isArray(data) || data.length === 0) {
        setToast({ open: true, type: "info", msg: "No attendance found for this student." });
      }
    } catch (err) {
      console.error(err);
      setToast({ open: true, type: "error", msg: "Failed to fetch summary." });
    } finally {
      setLoadingSummary(false);
    }
  }, [authHeader, buildParams]);

  const classKey = (r) =>
    [r.StudentID, r.SubCampusCode, r.DegreeCode, r.BranchCode, r.Semester].join("|");

  // ✅ Row-click toggle (class)
  const toggleSemester = async (row) => {
    const key = classKey(row);
    const currentlyOpen = !!openSemesters[key];
    setOpenSemesters((p) => ({ ...p, [key]: !currentlyOpen }));

    if (!currentlyOpen && !subjectData[key]) {
      setSubjectData((prev) => ({ ...prev, [key]: { loading: true, rows: [] } }));
      try {
        const { data } = await axios.get(`${API_URL}/api/studentattendance/subjects`, {
          ...authHeader,
          params: buildParams({
            StudentID: row.StudentID,
            SubCampusCode: row.SubCampusCode,
            DegreeCode: row.DegreeCode,
            BranchCode: row.BranchCode,
            SemesterCode: String(row.Semester),
          }),
        });
        setSubjectData((prev) => ({
          ...prev,
          [key]: { loading: false, rows: Array.isArray(data) ? data : [] },
        }));
      } catch (err) {
        console.error(err);
        setSubjectData((prev) => ({ ...prev, [key]: { loading: false, rows: [] } }));
        setToast({
          open: true,
          type: "error",
          msg: `Failed to load subjects for ${row.StudentID} • ${row.SubCampusCode} • Sem ${row.Semester}.`,
        });
      }
    }
  };

  // ===== Day Summary =====
  const fetchDaySummary = useCallback(async () => {
    if (!ensureStudentKey()) return;

    setLoadingDay(true);
    setDayRows([]);
    setOpenDayKeys({});
    setDayDates({});
    try {
      const { data } = await axios.get(`${API_URL}/api/studentattendance/day/summary`, {
        ...authHeader,
        params: buildParams(),
      });
      setDayRows(Array.isArray(data) ? data : []);
      if (!Array.isArray(data) || data.length === 0) {
        setToast({ open: true, type: "info", msg: "No day-wise summary found for this student." });
      }
    } catch (e) {
      console.error(e);
      setToast({ open: true, type: "error", msg: "Failed to fetch day summary." });
    } finally {
      setLoadingDay(false);
    }
  }, [authHeader, buildParams]);

  const dayKey = (r) =>
    [r.StudentID, r.SubCampusCode, r.DegreeCode, r.BranchCode, r.Semester].join("|");

  // ✅ Row-click toggle (day)
  const toggleDayRow = async (row) => {
    const key = dayKey(row);
    const nextOpen = !openDayKeys[key];
    setOpenDayKeys((p) => ({ ...p, [key]: nextOpen }));

    if (nextOpen && !dayDates[key]) {
      setDayDates((p) => ({ ...p, [key]: { loading: true, rows: [] } }));
      try {
        const { data } = await axios.get(`${API_URL}/api/studentattendance/day/dates`, {
          ...authHeader,
          params: buildParams({
            StudentID: row.StudentID,
            SubCampusCode: row.SubCampusCode,
            DegreeCode: row.DegreeCode,
            BranchCode: row.BranchCode,
            SemesterCode: String(row.Semester),
          }),
        });
        setDayDates((p) => ({
          ...p,
          [key]: { loading: false, rows: Array.isArray(data) ? data : [] },
        }));
      } catch (e) {
        console.error(e);
        setDayDates((p) => ({ ...p, [key]: { loading: false, rows: [] } }));
        setToast({ open: true, type: "error", msg: "Failed to fetch per-day rows." });
      }
    }
  };

  // ===== Auto-load =====
  useEffect(() => {
    if (!studentKey || !campusCode) return;
    if (viewMode === "class") fetchSummary();
    else fetchDaySummary();
  }, [viewMode, studentKey, campusCode, fetchSummary, fetchDaySummary]);

  // =======================
  // ✅ THEME styles (below theme)
  // =======================

  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
    backgroundColor: "transparent",
  };

  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };

  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "#fff",
    border: `1px solid ${BORDER}`,
  };

  const sectionSx = {
    ...surfaceSx,
    p: 2,
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
      opacity: 0.95,
    },
  };

  // ✅ Toggle buttons (like below theme)
  const toggleGroupSx = {
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(255,255,255,0.35)",
    backgroundColor: "rgba(255,255,255,0.12)",
    "& .MuiToggleButton-root": {
      textTransform: "none",
      fontWeight: 900,
      color: "white",
      border: "none",
      px: 1.6,
      py: 0.75,
      "&.Mui-selected": {
        backgroundColor: "rgba(255,255,255,0.24)",
        color: "white",
      },
      "&:hover": { backgroundColor: "rgba(255,255,255,0.18)" },
    },
  };

  // ✅ FIX: Fully-rounded tables even with horizontal scroll
  const tableCardSx = {
    ...surfaceSx,
    p: 0,
    overflow: "hidden", // clips header corners
  };

  const scrollClipSx = {
    overflowX: "auto",
    overflowY: "hidden",
    borderRadius: 2,
  };

  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
      py: 1,
      textAlign: "center",
    },
  };

  const zebraRowSx = (i) => ({
    backgroundColor: i % 2 === 0 ? ROW_ALT : "#FFFFFF",
    cursor: "pointer",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)", py: 1, textAlign: "center" },
  });

  const collapseSurfaceSx = {
    ...surfaceSx,
    p: 1.5,
    borderColor: "rgba(25,118,210,0.16)",
  };

  // ✅ Progress bar (below theme look)
  const progressSx = {
    height: 8,
    borderRadius: 999,
    width: "100%",
    backgroundColor: "rgba(25,118,210,0.10)",
    "& .MuiLinearProgress-bar": {
      backgroundColor: THEME_ORANGE,
      borderRadius: 999,
    },
  };

  // ✅ Attendance cell container (keeps bar next to %)
  const AttCell = ({ value }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 220, justifyContent: "center" }}>
      <Box sx={{ minWidth: 64, fontWeight: 900, color: "rgba(25,118,210,0.95)" }}>
        {pct(value).toFixed(2)}%
      </Box>
      <Box sx={{ flex: 1, minWidth: 120 }}>
        <LinearProgress variant="determinate" value={pct(value)} sx={progressSx} />
      </Box>
    </Box>
  );

  return (
    <Box sx={{ p: 2, backgroundColor: BG }}>
      <Paper elevation={5} sx={rootPaperSx}>
        {/* ✅ Orange header bar */}
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Student Attendance Report
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              {username ? `Logged in as ${username}` : "View attendance in Day or Class mode"}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {(loadingSummary || loadingDay) && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={18} sx={{ color: "white" }} />
                <Typography sx={{ fontSize: "0.85rem", opacity: 0.9 }}>Loading…</Typography>
              </Box>
            )}

            <ToggleButtonGroup
              size="small"
              value={viewMode}
              exclusive
              onChange={(_, v) => v && setViewMode(v)}
              sx={toggleGroupSx}
            >
              <ToggleButton value="day">Day View</ToggleButton>
              <ToggleButton value="class">Class View</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        {/* ✅ Body */}
        <Box sx={bodySx}>
          {/* ✅ View Mode section with left stripe (like below theme) */}
          <Paper variant="outlined" sx={sectionSx}>
            <Box sx={{ pl: 1 }}>
              <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 0.5 }}>
                View Mode
              </Typography>
              <Typography sx={{ fontSize: "0.85rem", opacity: 0.85, mb: 1 }}>
                Click a row to expand / collapse details
              </Typography>
            </Box>
          </Paper>

          {/* ===== CLASS VIEW ===== */}
          {viewMode === "class" && (
            <Paper elevation={0} sx={{ ...tableCardSx, mt: 2 }}>
              <Box sx={scrollClipSx}>
                <TableContainer component="div">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={theadRowSx}>
                        <TableCell sx={{ width: 56 }}>#</TableCell>
                        <TableCell>StudentID</TableCell>
                        <TableCell>SubCampus</TableCell>
                        <TableCell>Degree</TableCell>
                        <TableCell>Branch</TableCell>
                        <TableCell>Semester</TableCell>
                        <TableCell>TotalClasses</TableCell>
                        <TableCell>Attended</TableCell>
                        <TableCell>Absent</TableCell>
                        <TableCell>Attendance %</TableCell>
                        <TableCell>Fine</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {loadingSummary ? (
                        <TableRow>
                          <TableCell colSpan={11} sx={{ textAlign: "left" }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 2 }}>
                              <CircularProgress size={20} />
                              <Typography sx={{ fontWeight: 800 }}>Loading summary…</Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ) : summaryRows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={11} sx={{ p: 2, textAlign: "center", opacity: 0.8 }}>
                            No data.
                          </TableCell>
                        </TableRow>
                      ) : (
                        summaryRows.map((row, idx) => {
                          const key = classKey(row);
                          const isOpen = !!openSemesters[key];
                          const subj = subjectData[key];

                          return (
                            <React.Fragment key={`${key}-${idx}`}>
                              {/* ✅ CLICK ANYWHERE to open/close */}
                              <TableRow sx={zebraRowSx(idx)} onClick={() => toggleSemester(row)}>
                                <TableCell>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleSemester(row);
                                    }}
                                    sx={{
                                      border: "1px solid rgba(25,118,210,0.18)",
                                      borderRadius: 2,
                                      "&:hover": { backgroundColor: "rgba(25,118,210,0.06)" },
                                    }}
                                  >
                                    {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                  </IconButton>
                                </TableCell>

                                <TableCell>{row.StudentID}</TableCell>
                                <TableCell>{row.SubCampusCode}</TableCell>
                                <TableCell>{row.DegreeCode}</TableCell>
                                <TableCell>{row.BranchCode}</TableCell>
                                <TableCell>{row.Semester}</TableCell>
                                <TableCell>{Number(row.TotalClasses || 0)}</TableCell>
                                <TableCell>{Number(row.AttendedClasses || 0)}</TableCell>
                                <TableCell>{Number(row.AbsentClasses || 0)}</TableCell>
                                <TableCell sx={{ textAlign: "left" }}>
                                  <AttCell value={row.AttendancePercentage} />
                                </TableCell>
                                <TableCell>{Number(row.AttendanceFine || 0)}</TableCell>
                              </TableRow>

                              <TableRow>
                                <TableCell colSpan={11} sx={{ p: 0, backgroundColor: "rgba(240,120,40,0.03)" }}>
                                  <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                    <Box sx={{ m: 1 }}>
                                      <Box sx={collapseSurfaceSx}>
                                        <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                                          Attendance By Subject • {row.StudentID} (Sem {row.Semester})
                                        </Typography>

                                        {subj?.loading ? (
                                          <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 2 }}>
                                            <CircularProgress size={18} />
                                            <Typography sx={{ fontWeight: 800 }}>Loading subjects…</Typography>
                                          </Box>
                                        ) : (subj?.rows || []).length === 0 ? (
                                          <Typography sx={{ p: 2, opacity: 0.8 }}>No subject details.</Typography>
                                        ) : (
                                          // ✅ Nested table: rounded + clipped
                                          <Paper elevation={0} sx={{ ...tableCardSx, mt: 0 }}>
                                            <Box sx={scrollClipSx}>
                                              <TableContainer component="div">
                                                <Table size="small">
                                                  <TableHead>
                                                    <TableRow
                                                      sx={{
                                                        backgroundColor: "rgba(25,118,210,0.10)",
                                                        "& th": {
                                                          fontWeight: 900,
                                                          py: 1,
                                                          textAlign: "center",
                                                          whiteSpace: "nowrap",
                                                        },
                                                      }}
                                                    >
                                                      <TableCell>StudentID</TableCell>
                                                      <TableCell>Subject</TableCell>
                                                      <TableCell>SubjectName</TableCell>
                                                      <TableCell>Section</TableCell>
                                                      <TableCell>TeacherName</TableCell>
                                                      <TableCell>Scheduled</TableCell>
                                                      <TableCell>Attended</TableCell>
                                                      <TableCell>Absent</TableCell>
                                                      <TableCell>Attendance %</TableCell>
                                                      <TableCell>Fine</TableCell>
                                                    </TableRow>
                                                  </TableHead>

                                                  <TableBody>
                                                    {(subj?.rows || []).map((sRow, sIdx) => (
                                                      <TableRow
                                                        key={`${row.StudentID}-${row.Semester}-sub-${sIdx}`}
                                                        sx={{
                                                          backgroundColor:
                                                            sIdx % 2 === 0 ? "rgba(25,118,210,0.04)" : "#fff",
                                                          "&:hover": { backgroundColor: "rgba(25,118,210,0.08)" },
                                                          "& td": {
                                                            borderBottom: "1px solid rgba(25,118,210,0.10)",
                                                            py: 1,
                                                            textAlign: "center",
                                                          },
                                                        }}
                                                      >
                                                        <TableCell>{sRow.StudentID}</TableCell>
                                                        <TableCell>{sRow.SubjectCode}</TableCell>
                                                        <TableCell>{sRow.SubjectNameCode}</TableCell>
                                                        <TableCell>{sRow.Section}</TableCell>
                                                        <TableCell>{sRow.TeacherName}</TableCell>
                                                        <TableCell>{Number(sRow.TotalClasses || 0)}</TableCell>
                                                        <TableCell>{Number(sRow.AttendedClasses || 0)}</TableCell>
                                                        <TableCell>{Number(sRow.AbsentClasses || 0)}</TableCell>
                                                        <TableCell sx={{ textAlign: "left" }}>
                                                          <AttCell value={sRow.AttendancePercentage} />
                                                        </TableCell>
                                                        <TableCell>{Number(sRow.AttendanceFine || 0)}</TableCell>
                                                      </TableRow>
                                                    ))}
                                                  </TableBody>
                                                </Table>
                                              </TableContainer>
                                            </Box>
                                          </Paper>
                                        )}
                                      </Box>
                                    </Box>
                                  </Collapse>
                                </TableCell>
                              </TableRow>
                            </React.Fragment>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Paper>
          )}

          {/* ===== DAY VIEW ===== */}
          {viewMode === "day" && (
            <Paper elevation={0} sx={{ ...tableCardSx, mt: 2 }}>
              <Box sx={scrollClipSx}>
                <TableContainer component="div">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={theadRowSx}>
                        <TableCell sx={{ width: 56 }}>#</TableCell>
                        <TableCell>StudentID</TableCell>
                        <TableCell>SubCampus</TableCell>
                        <TableCell>Degree</TableCell>
                        <TableCell>Branch</TableCell>
                        <TableCell>Semester</TableCell>
                        <TableCell>WorkingDays</TableCell>
                        <TableCell>DaysAttended</TableCell>
                        <TableCell>DaysAbsent</TableCell>
                        <TableCell>Attendance %</TableCell>
                        <TableCell>Fine</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {loadingDay ? (
                        <TableRow>
                          <TableCell colSpan={11} sx={{ textAlign: "left" }}>
                            <Box sx={{ p: 2, display: "flex", gap: 2, alignItems: "center" }}>
                              <CircularProgress size={20} />
                              <Typography sx={{ fontWeight: 800 }}>Loading…</Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ) : dayRows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={11} sx={{ p: 2, textAlign: "center", opacity: 0.8 }}>
                            No data.
                          </TableCell>
                        </TableRow>
                      ) : (
                        dayRows.map((r, i) => {
                          const key = dayKey(r);
                          const isOpen = !!openDayKeys[key];
                          const dates = dayDates[key];

                          return (
                            <React.Fragment key={`${key}-${i}`}>
                              <TableRow sx={zebraRowSx(i)} onClick={() => toggleDayRow(r)}>
                                <TableCell>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleDayRow(r);
                                    }}
                                    sx={{
                                      border: "1px solid rgba(25,118,210,0.18)",
                                      borderRadius: 2,
                                      "&:hover": { backgroundColor: "rgba(25,118,210,0.06)" },
                                    }}
                                  >
                                    {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                  </IconButton>
                                </TableCell>

                                <TableCell>{r.StudentID}</TableCell>
                                <TableCell>{r.SubCampusCode}</TableCell>
                                <TableCell>{r.DegreeCode}</TableCell>
                                <TableCell>{r.BranchCode}</TableCell>
                                <TableCell>{r.Semester}</TableCell>
                                <TableCell>{Number(r.TotalWorkingDays || 0)}</TableCell>
                                <TableCell>{Number(r.DaysAttendedCollege || 0)}</TableCell>
                                <TableCell>{Number(r.DaysAbsent || 0)}</TableCell>
                                <TableCell sx={{ textAlign: "left" }}>
                                  <AttCell value={r.Percent} />
                                </TableCell>
                                <TableCell>{Number(r.AttendanceFine || 0)}</TableCell>
                              </TableRow>

                              <TableRow>
                                <TableCell colSpan={11} sx={{ p: 0, backgroundColor: "rgba(240,120,40,0.03)" }}>
                                  <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                    <Box sx={{ m: 1 }}>
                                      <Box sx={collapseSurfaceSx}>
                                        <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                                          Attendance By Day • {r.StudentID} (Sem {r.Semester})
                                        </Typography>
                                        {dates?.loading ? (
                                          <Box sx={{ p: 2, display: "flex", gap: 2, alignItems: "center" }}>
                                            <CircularProgress size={18} />
                                            <Typography sx={{ fontWeight: 800 }}>Loading days…</Typography>
                                          </Box>
                                        ) : (dates?.rows || []).length === 0 ? (
                                          <Typography sx={{ p: 2, opacity: 0.8 }}>No per-day rows.</Typography>
                                        ) : (
                                          <Paper elevation={0} sx={{ ...tableCardSx, mt: 0 }}>
                                            <Box sx={scrollClipSx}>
                                              <TableContainer component="div">
                                                <Table size="small">
                                                  <TableHead>
                                                    <TableRow
                                                      sx={{
                                                        backgroundColor: "rgba(25,118,210,0.10)",
                                                        "& th": {
                                                          fontWeight: 900,
                                                          py: 1,
                                                          textAlign: "center",
                                                          whiteSpace: "nowrap",
                                                        },
                                                      }}
                                                    >
                                                      <TableCell>StudentID</TableCell>
                                                      <TableCell>AttendanceDate</TableCell>
                                                      <TableCell>TotalClasses</TableCell>
                                                      <TableCell>PresentClasses</TableCell>
                                                      <TableCell>AbsentClasses</TableCell>
                                                      <TableCell>Attendance %</TableCell>
                                                      <TableCell>Fine</TableCell>
                                                    </TableRow>
                                                  </TableHead>

                                                  <TableBody>
                                                    {(dates.rows || []).map((d, j) => (
                                                      <TableRow
                                                        key={`${key}-${j}`}
                                                        sx={{
                                                          backgroundColor:
                                                            j % 2 === 0 ? "rgba(25,118,210,0.04)" : "#fff",
                                                          "&:hover": { backgroundColor: "rgba(25,118,210,0.08)" },
                                                          "& td": {
                                                            borderBottom: "1px solid rgba(25,118,210,0.10)",
                                                            py: 1,
                                                            textAlign: "center",
                                                          },
                                                        }}
                                                      >
                                                        <TableCell>{d.StudentID}</TableCell>
                                                        <TableCell>{convertToISTDate(d.AttendanceDate)}</TableCell>
                                                        <TableCell>{Number(d.TotalClasses || 0)}</TableCell>
                                                        <TableCell>{Number(d.PresentClasses || 0)}</TableCell>
                                                        <TableCell>{Number(d.AbsentClasses || 0)}</TableCell>
                                                        <TableCell sx={{ textAlign: "left" }}>
                                                          <AttCell value={d.PresentPercentage} />
                                                        </TableCell>
                                                        <TableCell>{Number(d.AttendanceFine || 0)}</TableCell>
                                                      </TableRow>
                                                    ))}
                                                  </TableBody>
                                                </Table>
                                              </TableContainer>
                                            </Box>
                                          </Paper>
                                        )}
                                      </Box>
                                    </Box>
                                  </Collapse>
                                </TableCell>
                              </TableRow>
                            </React.Fragment>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Paper>
          )}

          <Snackbar
            open={toast.open}
            autoHideDuration={3000}
            onClose={() => setToast((p) => ({ ...p, open: false }))}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert
              severity={toast.type === "error" ? "error" : toast.type === "info" ? "info" : "success"}
              sx={{
                borderRadius: 2,
                border: "1px solid rgba(25,118,210,0.18)",
              }}
              onClose={() => setToast((p) => ({ ...p, open: false }))}
            >
              {toast.msg}
            </Alert>
          </Snackbar>
        </Box>
      </Paper>
    </Box>
  );
}
